import { createFileRoute, useLocation } from '@tanstack/react-router'
import { Home } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { AgentDetail } from '@/features/agents/components/AgentDetail'
import { useAgent } from '@/features/agents/hooks/useAgents'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/shared/components/ui/breadcrumb'
import { useBreadcrumbs } from '@/shared/hooks/useBreadcrumbs'

interface AgentDetailRouteState {
  isNewlyCreated?: boolean
}

export const Route = createFileRoute('/_auth/agents/$agentId')({
  component: AgentDetailRoute,
})

function AgentDetailRoute() {
  const { agentId } = Route.useParams()
  const location = useLocation()
  const { setBreadcrumbs } = useBreadcrumbs()

  // Check if this is a newly created agent from navigation state
  const navigationState = location.state as AgentDetailRouteState
  const isNewlyCreated = navigationState?.isNewlyCreated || false

  // Fetch agent data for breadcrumbs
  const { data: agent } = useAgent(agentId)

  const breadcrumbsContent = useMemo(() => {
    if (!agent) return null

    const pageTitle = isNewlyCreated ? `Configure ${agent.name}` : agent.name

    return (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink to='/dashboard' className='flex items-center gap-1'>
              <Home className='w-4 h-4' />
              Dashboard
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink to='/agents'>Agents</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{pageTitle}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    )
  }, [agent, isNewlyCreated])

  useEffect(() => {
    if (breadcrumbsContent) {
      setBreadcrumbs(breadcrumbsContent)
    }
  }, [setBreadcrumbs, breadcrumbsContent])

  return <AgentDetail agentId={agentId} isNewlyCreated={isNewlyCreated} />
}
